import { ChevronRight, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { RateMultiplier } from '@/types/RateMultiplier';
import { decimalToHHMM, type MultiplierLiveValues } from './multiplierUtils';

interface MultiplierListItemProps {
  multiplier: RateMultiplier;
  isStraightTime: boolean;
  isExpanded?: boolean;
  onClick?: () => void;
  /** When provided, display these values instead of the saved multiplier (for live editing) */
  liveOverrides?: MultiplierLiveValues;
}

interface ThresholdBadgeProps {
  hours: number;
  type: 'shift' | 'week';
}

export function ThresholdBadge({ hours, type }: ThresholdBadgeProps) {
  const formatted = decimalToHHMM(hours);
  const isShift = type === 'shift';

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] sm:text-[12px] font-mono font-semibold leading-none',
        isShift
          ? 'bg-primary/15 text-primary-text'
          : 'bg-purple-500/15 text-purple-600 dark:text-purple-400'
      )}
    >
      <span className="text-[9px]" aria-hidden="true">{isShift ? '⏱' : '📅'}</span>
      <span className="lg:hidden">{formatted}h</span>
      <span className="hidden lg:inline">
        After {formatted} hrs {isShift ? 'per shift' : 'per week'}
      </span>
    </span>
  );
}

export function MultiplierListItem({ multiplier, isStraightTime, isExpanded, onClick, liveOverrides }: MultiplierListItemProps) {
  const displayName = liveOverrides?.name ?? multiplier.name ?? multiplier.code;
  const displayRate = liveOverrides?.rate ?? multiplier.defaultMultiplier;
  const hasShiftThreshold = liveOverrides
    ? liveOverrides.shiftEnabled && liveOverrides.shiftThreshold != null
    : multiplier.defaultPerShiftThresholdHours != null;
  const shiftHours = liveOverrides
    ? liveOverrides.shiftThreshold
    : multiplier.defaultPerShiftThresholdHours;
  const hasWeeklyThreshold = liveOverrides
    ? liveOverrides.weeklyEnabled && liveOverrides.weeklyThreshold != null
    : multiplier.defaultPerWeekThresholdHours != null;
  const weeklyHours = liveOverrides
    ? liveOverrides.weeklyThreshold
    : multiplier.defaultPerWeekThresholdHours;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isStraightTime && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onClick?.();
    }
  };

  return (
    <div
      className={cn(
        'group grid grid-cols-[44px_1fr_auto_auto] items-center gap-2 px-3 py-2.5',
        isStraightTime
            ? 'bg-muted/40 dark:bg-transparent border border-border/20 dark:border-border/30 rounded'
            : isExpanded
              ? 'bg-surface-2 dark:bg-transparent border-b border-border/40'
              : 'border border-border/60 bg-surface-1 dark:bg-transparent rounded equipment-card cursor-pointer hover:-translate-y-[1px] hover:shadow-md hover:border-primary/30 active:translate-y-[1px] active:shadow-none transition-[background-color,transform,box-shadow,border-color] duration-150'
        )}
      role={isStraightTime ? undefined : 'button'}
      tabIndex={isStraightTime ? undefined : 0}
      onClick={isStraightTime ? undefined : onClick}
      onKeyDown={isStraightTime ? undefined : handleKeyDown}
    >
      {/* Rate column */}
      <div
        className={cn(
          'font-mono text-sm font-bold tabular-nums',
          isStraightTime ? 'text-muted-foreground/60' : 'text-primary-text'
        )}
      >
        {displayRate !== null && displayRate !== undefined
          ? `${displayRate}x`
          : '—'}
      </div>

      {/* Name column */}
      <div className={cn(
        'font-heading text-[13px] uppercase tracking-wide truncate',
        isStraightTime ? 'text-muted-foreground' : 'text-foreground'
      )}>
        {displayName}
      </div>

      {/* Threshold badges / base rate label */}
      <div className="flex flex-wrap items-center justify-end gap-1.5">
        {isStraightTime ? (
          <span className="text-[10px] sm:text-[12px] font-mono uppercase tracking-wider text-muted-foreground">
            Base rate
          </span>
        ) : (
          <>
            {hasShiftThreshold && shiftHours != null && (
              <ThresholdBadge hours={shiftHours} type="shift" />
            )}
            {hasWeeklyThreshold && weeklyHours != null && (
              <ThresholdBadge hours={weeklyHours} type="week" />
            )}
            {!hasShiftThreshold && !hasWeeklyThreshold && (
              <span className="text-[10px] sm:text-[12px] text-muted-foreground italic">No thresholds</span>
            )}
          </>
        )}
      </div>

      {/* Chevron column */}
      <div className="w-4 flex justify-center">
        {!isStraightTime && (
          isExpanded
            ? <ChevronDown className="h-4 w-4 text-primary" />
            : <ChevronRight className="h-4 w-4 text-muted-foreground/80 group-active:text-primary transition-colors" />
        )}
      </div>
    </div>
  );
}
